import './forte-bank.scss';

import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { Button, DocumentController } from '@/components/Shared/ui';
import { ReloadOutlined } from '@/media/antd-icons';
import { checkIsValidJSON } from '@/services/helpers/checkIsValidJSON';
import { useAppDispatch, useAppSelector } from '@/store';
import { getForteStatus } from '@/store/offerStep.slice';
import type { FileType } from '@/types/uploadFiles';

import { FILE_NAMES } from '../../constants';
import { GrnFactoryCheck } from '../../entities';
import useInstructionsText from '../../hooks/useInstructionsText';
import type { SendDocumentsHandler } from '../../widgets';
import useForteStatus from './hooks/useForteStatus';

interface IProps {
  isSubmitLoading: boolean;
  onSubmit: SendDocumentsHandler<ForteAdditionalDataInterface>;
}

const CHOSEN_FILES = [
  'forteSpouseAgreement',
  'forteCommitmentLetter',
  'spouseConsent',
  'spouse_consent',
  'forteNoSpouseAgreement'
];

const CHOSEN_FILES_NAMES = {
  forteSpouseAgreement: 'Согласие супруга(и)',
  forteCommitmentLetter: 'Гарантийное письмо',
  spouseConsent: 'Согласие супруга(и) / Отсутствие супруга(и) (подписанный)',
  spouse_consent: 'Согласие супруга(и) / Отсутствие супруга(и)',
  forteNoSpouseAgreement: 'Отсутствие супруга(и)'
};

const ForteBank: React.FC<IProps> = ({ isSubmitLoading, onSubmit }) => {
  const dispatch = useAppDispatch();
  const {
    selectedBank,
    attachedFiles,
    isVehicleInfoValid,
    isGrnFactoryCheckPassed
  } = useAppSelector((state) => state.financingStep);
  const { banksOffers } = useAppSelector((state) => state.offerStep);
  const { tsModalValues } = useAppSelector((state) => state.newOrder);

  useInstructionsText();

  const isHasFinancedOffer = useMemo(
    () => banksOffers.some((offer) => offer.status.code === 'financed'),
    [banksOffers]
  );

  const files = useMemo(() => {
    if (!attachedFiles) return [];
    return (
      attachedFiles
        .filter((value, index, self) => {
          return (
            self.findIndex((item) => item.type === value.type) === index &&
            CHOSEN_FILES.includes(value.type)
          );
        })
        // Сортировка в порядке установленном в константе CHOSEN_FILES
        .sort(
          (a, b) => CHOSEN_FILES.indexOf(a.type) - CHOSEN_FILES.indexOf(b.type)
        )
    );
  }, [attachedFiles]);
  const techPassport = useMemo(
    () =>
      attachedFiles?.find((value) => {
        return value.type === 'tech_passport';
      }),
    [attachedFiles]
  );

  const { updateStatusLoading, handleUpdateForteStatus } = useForteStatus();

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm<{ spouseConsent: FileType }>({
    mode: 'all',
    delayError: 200
  });

  const prepareSubmit = (values: Record<string, FileType>) => {
    const isResponseApprovedBodyValid = checkIsValidJSON(
      selectedBank?.responseApprovedBody || ''
    );
    const responseApprovedBody = isResponseApprovedBodyValid
      ? JSON.parse(selectedBank?.responseApprovedBody || '{}')
      : {};

    onSubmit(values, {
      fileUuidsToDownloadOnEmpty: {
        spouseConsent: files.find((item) => item.type === 'spouseConsent')?.uuid
      },
      additionalData: {
        spouse_agreement_id: responseApprovedBody?.spouse_agreement_id || '',
        commitment_letter_id: responseApprovedBody?.commitment_letter_id || '',
        vin_code: tsModalValues?.vin || '',
        marital_status_id: responseApprovedBody?.marital_status_id || '',
        no_spouse_agreement_id:
          responseApprovedBody?.no_spouse_agreement_id || ''
      }
    });
  };

  return (
    <>
      {!isHasFinancedOffer && (
        <div className="forte-bank__actions">
          <Button
            className="forte-bank__update-status"
            size="small"
            onClick={handleUpdateForteStatus}
            isLoading={updateStatusLoading}
            isReversed
          >
            Обновить статус
            {!updateStatusLoading && <ReloadOutlined />}
          </Button>
        </div>
      )}

      <form
        className="financing-step__form"
        onSubmit={handleSubmit(prepareSubmit)}
      >
        {techPassport && (
          <DocumentController
            fileLink={techPassport.uuid}
            title={FILE_NAMES[techPassport.type as keyof typeof FILE_NAMES]}
            downloadable
          />
        )}
        {files
          ? files.map((file, index) => {
              const className =
                // Элементы отсортированы в нужном порядке сверху
                index < 2
                  ? 'financing-step__field--half'
                  : 'financing-step__field';
              return (
                <React.Fragment key={file.type}>
                  <DocumentController
                    title={
                      CHOSEN_FILES_NAMES[
                        file.type as keyof typeof CHOSEN_FILES_NAMES
                      ]
                    }
                    fileLink={file.uuid}
                    preDownloadEvent={async () => {
                      if (
                        file.type === 'forteCommitmentLetter' &&
                        (selectedBank?.status.code === 'approved' ||
                          selectedBank?.status.code === 'waiting_of_financing')
                      ) {
                        await dispatch(
                          getForteStatus({
                            extUuid: selectedBank?.extId || '',
                            controlClientDecision: true
                          })
                        );
                      }
                    }}
                    downloadable
                    wrapperClassName={className}
                  />
                  {file.type === 'forteCommitmentLetter' && (
                    <GrnFactoryCheck
                      grnFileLink={file.link}
                      grnFileType={file.type}
                    />
                  )}
                </React.Fragment>
              );
            })
          : selectedBank?.status.code === 'financed' && (
              <p className="financing-step__no-documents">Нет документов</p>
            )}

        {selectedBank?.status.code !== 'financed' && isVehicleInfoValid && (
          <>
            <DocumentController
              control={control}
              name="spouseConsent"
              title="Согласие супруга(и) / Отсутствие супруга(и)"
              uploadable
              isRequired={!files.some((item) => item.type === 'spouseConsent')}
            />

            <Button
              className="financing-step__submit"
              variant="filled-red"
              disabled={
                Object.values(errors).length !== 0 || !isGrnFactoryCheckPassed
              }
              isLoading={isSubmitLoading}
            >
              Отправить документы
            </Button>
          </>
        )}
      </form>
    </>
  );
};

export default ForteBank;

interface ForteAdditionalDataInterface {
  spouse_agreement_id: string;
  commitment_letter_id: string;
  vin_code: string;
  marital_status_id: string;
  no_spouse_agreement_id: string;
}
